/**
 * A Korean IME, for people who do not have one.
 *
 * ── How this differs from `hangul.ts`, which it sits beside ─────────────────
 * That file GUESSES. Someone typed `wndrnr` meaning `중국`, their OS keyboard
 * was in the wrong mode, and it offers the conversion after the fact — a card
 * under the box, accepted with the space bar, declined with Escape. Every
 * safeguard in it exists because it has to decide whether the person meant
 * Korean at all: `with` is also a valid run of keystrokes.
 *
 * This file does not guess, because it is not asked to. The reader has chosen
 * Korean as the input language and switched the keyboard on, so `r` is `ㄱ` —
 * not "probably ㄱ". Nothing is offered, nothing is dismissed, and no card
 * appears: the syllable simply forms in the box as it is typed, which is what
 * every real IME does and what the owner asked for.
 *
 * Both share ONE layout table and ONE composition automaton, in hangul.ts.
 * That is deliberate: two copies of dubeolsik that disagreed by one key would
 * be a bug nobody could see, because each half would look right on its own.
 *
 * ── Composing incrementally ─────────────────────────────────────────────────
 * `keystrokesToHangul` composes a whole string in one pass, which an IME cannot
 * do: at the moment `ㄱ` is typed the reader is still deciding, and `가` `각`
 * `가구` are all still reachable from it. So the unit here is ONE jamo landing
 * on whatever is already in front of the caret. The previous character is taken
 * apart, the jamo is run through the same automaton, and the result replaces
 * it — which is how `ㄱ` becomes `가` becomes `각` becomes `가구` without ever
 * leaving a wrong character on screen in between.
 */

import { CHO, JUNG, JONG, VOWEL_PAIRS, FINAL_PAIRS, FINAL_SPLIT, jamoFor, renderBlock, type Block } from './hangul';

const EMPTY: Block = { cho: '', jung: '', jong: '' };
const SYLLABLE_START = 0xac00;
const SYLLABLE_END = 0xd7a3;

/**
 * A finished syllable taken back apart, or a lone jamo put in the slot it would
 * occupy.
 *
 * The order of the two `includes` matters: `ㄱ` is a legal opening consonant
 * AND a legal final one, and standing alone it is always the opening — anyone
 * who has typed `ㄱ` is on their way to `가`, not to a syllable with no vowel.
 */
export function decompose(ch: string): Block | null {
  if (!ch) return null;
  const code = ch.charCodeAt(0);
  if (code >= SYLLABLE_START && code <= SYLLABLE_END) {
    const i = code - SYLLABLE_START;
    return {
      cho: CHO[Math.floor(i / 588)],
      jung: JUNG[Math.floor((i % 588) / 28)],
      jong: JONG[i % 28],
    };
  }
  if (CHO.includes(ch)) return { cho: ch, jung: '', jong: '' };
  if (JUNG.includes(ch)) return { cho: '', jung: ch, jong: '' };
  if (JONG.includes(ch)) return { cho: '', jung: '', jong: ch };
  return null;
}

const isVowel = (j: string) => JUNG.includes(j);

/**
 * One jamo, landing on one block.
 *
 * The body mirrors the loop in `keystrokesToHangul` exactly, including the
 * stolen final — `속` followed by `ㅏ` is `소가`, never `속아` — because the two
 * have to agree. Anything that composes differently here would mean the
 * suggestion card and the keyboard produced different Korean from the same
 * keys, and only one of them could be right.
 */
function step(start: Block, jamo: string): { emit: string; block: Block } {
  let b: Block = { ...start };
  let emit = '';
  const flush = () => { emit += renderBlock(b); b = { ...EMPTY }; };

  if (isVowel(jamo)) {
    if (b.jong) {
      const split = FINAL_SPLIT[b.jong];
      const stolen = split ? split[1] : b.jong;
      b.jong = split ? split[0] : '';
      flush();
      b = { cho: stolen, jung: jamo, jong: '' };
    } else if (b.jung) {
      const pair = VOWEL_PAIRS[b.jung + jamo];
      if (pair) {
        b.jung = pair;
      } else {
        flush();
        b = { cho: '', jung: jamo, jong: '' };
      }
    } else if (b.cho) {
      b.jung = jamo;
    } else {
      flush();
      emit += jamo;
    }
    return { emit, block: b };
  }

  if (!b.cho && !b.jung) {
    if (CHO.includes(jamo)) b.cho = jamo;
    else { flush(); emit += jamo; }
  } else if (b.cho && !b.jung) {
    flush();
    b.cho = jamo;
  } else if (!b.jong) {
    if (JONG.includes(jamo)) b.jong = jamo;
    else { flush(); b.cho = jamo; }
  } else {
    const pair = FINAL_PAIRS[b.jong + jamo];
    if (pair) {
      b.jong = pair;
    } else {
      flush();
      b.cho = jamo;
    }
  }
  return { emit, block: b };
}

/** An edit to apply at the caret: delete `back` characters, then type `insert`. */
export interface ImeEdit {
  back: number;
  insert: string;
}

/**
 * What typing `jamo` should do, given the text in front of the caret.
 *
 * Only ever looks at the LAST character, and only ever replaces that one, so
 * the cost does not grow with the size of the box and a 5,000-character
 * document types as fast as an empty one.
 *
 * ⚠️ It composes with whatever is there, including Korean that arrived by
 * paste. A syllable a desktop IME would consider closed can still take a final
 * here — paste `한`, type `ㅏ`, and you get `하나`. That is the right trade:
 * treating every previous character as closed would break the ordinary case
 * (a syllable this keyboard typed a moment ago) to protect a rare one, and the
 * ordinary case is someone typing a sentence.
 */
export function typeJamo(before: string, jamo: string): ImeEdit {
  const block = decompose(before.slice(-1));
  if (!block) return { back: 0, insert: jamo };
  const { emit, block: rest } = step(block, jamo);
  return { back: 1, insert: emit + renderBlock(rest) };
}

/**
 * The jamo a physical key produces, or null if the key is not on the layout.
 *
 * Digits, punctuation and the space bar are not on it on purpose: those keys
 * are the same on a Korean keyboard as on any other, so they must pass through
 * untouched rather than be swallowed by the IME.
 */
export function jamoForKey(key: string): string | null {
  if (key.length !== 1) return null;
  return jamoFor(key) ?? null;
}

// ── The on-screen keyboard ──────────────────────────────────────────────────
//
// The same three rows a Korean keyboard has, in the same order, so that anyone
// looking at the panel is learning the layout under their own fingers rather
// than a private arrangement of this app's. Held here beside the mapping it is
// drawn from — a picture of the layout that could drift from the layout would
// be worse than no picture.

export interface KeyCap {
  /** The jamo this key types in its unshifted state. */
  jamo: string;
  /** The Latin key it lives on, shown small on the cap so the mapping is learnable. */
  key: string;
  /** The jamo the same key types with Shift, where there is one. */
  shifted?: string;
  /** The Latin key that produces `shifted` — always the uppercase of `key`. */
  shiftKey?: string;
}

function caps(keys: string): KeyCap[] {
  return [...keys].map((key) => {
    const upper = key.toUpperCase();
    const shifted = jamoFor(upper);
    const plain = jamoFor(key)!;
    return shifted && shifted !== plain
      ? { jamo: plain, key, shifted, shiftKey: upper }
      : { jamo: plain, key };
  });
}

/** Dubeolsik, the three letter rows. Consonants left, vowels right, as on the real thing. */
export const KEYBOARD_ROWS: KeyCap[][] = [
  caps('qwertyuiop'),
  caps('asdfghjkl'),
  caps('zxcvbnm'),
];
